'use client';
import { useOptimistic, useTransition } from 'react';
import { BsFilter } from 'react-icons/bs';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import Props from '@/components/app/archive/components/sort/type';
import ViewMode from '@/components/app/archive/components/view-mode';
import { FILTER_KEYS, SORT_MODE } from '@/components/app/archive/helpers';
import { useFilterParams } from '@/components/app/archive/helpers/hooks';
import IconTextCombo from '@/components/shared/components/icon-text-combo';

import {
  DATA_CY_ARCHIVE_SORT_ITEM,
  DATA_CY_ARCHIVE_SORT_ITEM_CONTAINER,
  DATA_CY_SORT_CONTAINER,
} from '@/constant/cypress';
import ICON_SIZE from '@/constant/icon-size-color';
import { SORT_ITEMS } from '@/constant/iterable-items';
import localization, { pageLevelLocalization } from '@/constant/localization';

const { archive } = pageLevelLocalization;
const SORT_MODE_MOST_RELEVANT = 'most_relevant';

export default function Sort({ initialFilters, searchPage }: Props) {
  const { updateParams } = useFilterParams({ searchParams: initialFilters });
  const [activeSort, setActiveSort] = useOptimistic<
    SORT_MODE | typeof SORT_MODE_MOST_RELEVANT
  >(
    initialFilters[FILTER_KEYS.MOST_RELEVANT]
      ? SORT_MODE_MOST_RELEVANT
      : searchPage && !initialFilters[FILTER_KEYS.SORT]
        ? SORT_MODE_MOST_RELEVANT
        : (initialFilters[FILTER_KEYS.SORT] ?? SORT_MODE.MOST_UPDATE),
  );

  let [isPending, startTransition] = useTransition();
  const handleSortClick = (key) => {
    startTransition(() => {
      setActiveSort(key);
      if (key === SORT_MODE.SPECIAL_SALE) {
        updateParams(FILTER_KEYS.HAS_DISCOUNT, true);
      } else {
        updateParams(FILTER_KEYS.HAS_DISCOUNT, false);
      }
      if (key === SORT_MODE_MOST_RELEVANT) {
        updateParams(FILTER_KEYS.SORT, '');
        updateParams(FILTER_KEYS.MOST_RELEVANT, true);
      } else {
        updateParams(FILTER_KEYS.SORT, key);
        updateParams(FILTER_KEYS.MOST_RELEVANT, '');
      }
    });
  };
  return (
    <Container
      className='relative my-6 flex items-center bg-[#FFFFFF] pr-4 shadow-[0px_10px_25px_0px_#5B5E6812] md:rounded-sm'
      data-pending={isPending ? '' : undefined}
      data-cy={DATA_CY_SORT_CONTAINER}
    >
      <IconTextCombo
        title={archive.sort}
        Icon={<BsFilter size={ICON_SIZE.md} />}
        wrapperClassName='max-w-[100px] text-[#424244] whitespace-nowrap!'
      />
      <Container
        data-cy={DATA_CY_ARCHIVE_SORT_ITEM_CONTAINER}
        className='no-scrollbar flex max-w-fit gap-4 overflow-x-auto'
      >
        {searchPage && 
          initialFilters[FILTER_KEYS.FIND] && 
          initialFilters[FILTER_KEYS.FIND] !== '' && (
          <Container
            data-cy={DATA_CY_ARCHIVE_SORT_ITEM}
            key='most-relevant'
            className={cn(
              'min-w-fit cursor-pointer py-4 text-[15px] text-[#878F9B] transition duration-300 first:mr-4 last:ml-4 hover:text-[#5B5C60]',
              activeSort === SORT_MODE_MOST_RELEVANT &&
                'border-b-2 border-[#FF9606] font-bold text-[#5B5C60]',
            )}
            onClick={() => {
              handleSortClick(SORT_MODE_MOST_RELEVANT);
            }}
          >
            {localization.mostRelevant}
          </Container>
        )}
        {SORT_ITEMS.map((item) => (
          <Container
            data-cy={DATA_CY_ARCHIVE_SORT_ITEM}
            key={item.id}
            className={cn(
              'min-w-fit cursor-pointer py-4 text-[15px] text-[#878F9B] transition duration-300 first:mr-4 last:ml-4 hover:text-[#5B5C60]',
              activeSort === item.sortKey &&
                'border-b-2 border-[#FF9606] font-bold text-[#5B5C60]',
            )}
            onClick={() => {
              handleSortClick(item.sortKey);
            }}
          >
            {item.label}
          </Container>
        ))}
      </Container>
      {initialFilters[FILTER_KEYS.VIEW_DEMO] !== true && <ViewMode />}
    </Container>
  );
}
